import * as React from "react";
import { useDispatch } from "react-redux";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/logo.svg";
import PrivacyPolicyText from "../../../component/PrivacyPolicyText";
import { resendCode, verifyCode } from 'api';
import { set_message } from "redux/reducer/message";
import { set_user, set_plan } from "redux/reducer/user";
import Section from "../home/components/Section";
import { otpDigitFieldSx, getOtpDigitInputProps, OTP_AUTOFILL_INPUT_PROPS, scheduleOtpAutofocus } from "component/Input/darkFieldStyles";
import {
  getVerificationRedirectPath,
  isVerificationRequiredError,
  persistVerificationUser,
} from 'utils/verificationGate';

const OTP_LENGTH = 6;

export const OTP = () => {
  const [otp, setOtp] = React.useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [counter, setCount] = React.useState(0);
  const { state } = useLocation();
  const username = state?.username;
  const inputRefs = React.useRef([]);
  const pendingFocusRef = React.useRef(null);
  const submittedOtpRef = React.useRef('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (otp.length < OTP_LENGTH) {
      submittedOtpRef.current = '';
    }
  }, [otp]);

  React.useEffect(() => {
    if (!username) {
      navigate("/login");
      return;
    }
    localStorage.removeItem('user-welcome');
    return scheduleOtpAutofocus(() => inputRefs.current[0]);
  }, [username, navigate]);

  const focusDigit = React.useCallback((index) => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus();
      inputRefs.current[index]?.select?.();
    }
  }, []);

  const queueFocus = React.useCallback((index) => {
    pendingFocusRef.current = index;
  }, []);

  React.useLayoutEffect(() => {
    if (pendingFocusRef.current === null) return;
    const index = pendingFocusRef.current;
    pendingFocusRef.current = null;
    focusDigit(index);
  });

  const startTimer = React.useCallback(() => {
    setCount(30);
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev === 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const applyDigits = React.useCallback((digits) => {
    const next = digits.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(next);
    queueFocus(Math.min(next.length, OTP_LENGTH - 1));
  }, [queueFocus]);

  const handleKeyDown = (event, index) => {
    const { key } = event;

    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      setOtp((prev) => {
        if (prev[index]) {
          return prev.slice(0, index) + prev.slice(index + 1);
        }
        if (index > 0) {
          queueFocus(index - 1);
          return prev.slice(0, index - 1) + prev.slice(index);
        }
        return '';
      });
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      setOtp((prev) => (
        (prev.slice(0, index) + key + prev.slice(index + 1)).slice(0, OTP_LENGTH)
      ));
      if (index < OTP_LENGTH - 1) {
        queueFocus(index + 1);
      }
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      focusDigit(index - 1);
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      focusDigit(index + 1);
      return;
    }

    if (key.length === 1) {
      event.preventDefault();
    }
  };

  const handleChange = (event, index) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    setOtp((prev) => (
      (prev.slice(0, index) + digit + prev.slice(index + 1)).slice(0, OTP_LENGTH)
    ));
    if (index < OTP_LENGTH - 1) {
      queueFocus(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    if (pasted) applyDigits(pasted);
  };

  const handleVerify = React.useCallback(() => {
    setLoading(true);
    verifyCode(username, otp)
      .then((res) => {
        dispatch(set_user(res.data.user));
        dispatch(set_plan(res.data.plan));
        setTimeout(() => {
          navigate('/dashboard/main');
        }, 200);
      })
      .catch((err) => {
        submittedOtpRef.current = '';
        if (isVerificationRequiredError(err)) {
          const payload = err.response.data;
          persistVerificationUser(payload.user);
          dispatch(set_user(payload.user));
          navigate(getVerificationRedirectPath(payload));
          return;
        }
        if (err.response?.status < 500 && err.response?.status >= 400) {
          setError(err.response.data.msg);
        } else {
          setError('something went wrong !');
        }
        setOtp('');
        queueFocus(0);
        setLoading(false);
      });
  }, [username, otp, dispatch, navigate, queueFocus]);

  React.useEffect(() => {
    if (otp.length !== OTP_LENGTH || loading) return;
    if (submittedOtpRef.current === otp) return;
    submittedOtpRef.current = otp;
    handleVerify();
  }, [otp, handleVerify, loading]);

  const resend_code = () => {
    if (counter) return;
    setOtp('');
    setError(null);
    queueFocus(0);
    resendCode(username)
      .then(() => {
        startTimer();
        dispatch(set_message('CODE sent to ' + username));
      })
      .catch((err) => dispatch(set_message(err.response.data.msg)));
  };

  const renderDigit = (index) => (
    <TextField
      key={index}
      variant="outlined"
      autoFocus={index === 0}
      error={Boolean(error)}
      disabled={loading}
      onKeyDown={(event) => handleKeyDown(event, index)}
      onChange={(event) => handleChange(event, index)}
      onPaste={handlePaste}
      type="tel"
      name={`otp-${index + 1}`}
      value={otp[index] || ""}
      inputRef={(el) => { inputRefs.current[index] = el; }}
      inputProps={getOtpDigitInputProps(index)}
      sx={otpDigitFieldSx}
    />
  );

  return (
    <Section compact sx={{ bgcolor: '#21232E', pb: 4 }}>
      <Box
        component="form"
        className="login-form-dark"
        sx={{
          maxWidth: 400,
          width: "92%",
          color: '#fff',
        }}
        mx="auto"
        autoComplete="off"
      >
        <Box
          display="flex"
          sx={{
            height: "15vh",
            minHeight: '130px',
            justifyContent: "center",
            alignItems: "center",
            marginTop: { xs: '10%', md: '20%' },
          }}
        >
          <img src={logo} height="150px" alt="logo" />
        </Box>

        <Typography sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} mt={3} variant="h6">
          Enter the 6 digit CODE we sent to you
        </Typography>
        <Typography sx={{ textAlign: 'center', fontWeight: 600, color: '#95F205', mt: 1 }} variant="body1">
          CODE Sent...
        </Typography>

        {error && (
          <Typography sx={{ color: '#f87171', textAlign: 'center', mt: 1 }}>{error}</Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, mt: 2, position: 'relative' }}>
          <input
            {...OTP_AUTOFILL_INPUT_PROPS}
            aria-hidden="true"
            tabIndex={-1}
            onChange={(event) => applyDigits(event.target.value)}
            style={{
              position: 'absolute',
              opacity: 0,
              width: 1,
              height: 1,
              border: 0,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          />
          {Array.from({ length: OTP_LENGTH }, (_, index) => renderDigit(index))}
        </Box>

        <Box my={3} textAlign="center">
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Didn&apos;t receive it?{' '}
            {counter ? (
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                Please retry in {counter}s
              </Typography>
            ) : (
              <Button
                onClick={resend_code}
                sx={{
                  color: '#60a5fa',
                  textTransform: 'none',
                  p: 0,
                  minWidth: 0,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                Resend
              </Button>
            )}{' '}
            my verification code
          </Typography>
        </Box>

        <Box sx={{ color: 'rgba(255,255,255,0.75)', '& a': { color: '#60a5fa' } }}>
          <PrivacyPolicyText />
        </Box>
      </Box>
    </Section>
  );
};

export default OTP;
